/**
 * Agent Detail Dialog Component
 *
 * Shows detailed information about an agent.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';

export default function AgentDetailDialog({ open, agent, onClose }) {
  if (!agent) return null;

  const config = agent.default_config || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{agent.name}</Typography>
          <Button startIcon={<CloseIcon />} onClick={onClose}>
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Basic Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Agent ID
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
            {agent.agent_id}
          </Typography>

          {agent.description && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {agent.description}
              </Typography>
            </>
          )}

          {agent.category && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Category
              </Typography>
              <Chip label={agent.category} size="small" color="primary" sx={{ mb: 2 }} />
            </>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Configuration */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Default Configuration
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Model"
                secondary={config.model || 'Not specified'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Temperature"
                secondary={config.temperature ?? 'Not specified'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Max Tokens"
                secondary={config.max_tokens || 'Not specified'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Provider"
                secondary={config.llm_provider || 'Not specified'}
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Supported Tasks */}
        {agent.supported_tasks && agent.supported_tasks.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Supported Tasks
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {agent.supported_tasks.map((task, index) => (
                <Chip key={index} label={task} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Output Format */}
        {agent.output_format && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom>
                Output Format
              </Typography>
              <Chip label={agent.output_format} size="small" />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
